import React, { useEffect, useState } from "react";
import { Alert, Button, Form, FormGroup, Input, Label } from "reactstrap";
import { getData, postData } from "../../helpers/url-request";

const modes = [
  ["Legacy", "Existing PIERS Authentication"],
  ["Entra", "Microsoft Entra ID"],
  ["EntraWithLegacyFallback", "Microsoft Entra ID + PIERS fallback"],
];

const FirmAuthenticationSettings = ({ firmId }) => {
  const [model, setModel] = useState({
    authenticationMode: "Legacy",
    externalIdentityProvisioningMode: "PiersManaged",
    externalTenantId: "",
    tenantDisplayName: "",
    federationProviderId: "",
    federationIssuer: "",
    federationTenantId: "",
    connectionStatus: "Not connected",
    connectedAt: null,
  });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    getData(`/firmidentity/${firmId}`)
      .then((response) => {
        if (!response?.status)
          throw new Error(
            response?.message || "Unable to load authentication settings."
          );
        setModel((current) => ({
          ...current,
          ...response.data,
          externalTenantId: response.data.externalTenantId || "",
          tenantDisplayName: response.data.tenantDisplayName || "",
        }));
      })
      .catch((loadError) => setError(loadError.message));

  useEffect(() => {
    if (firmId) load();
  }, [firmId]);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const response = await postData(`/firmidentity/${firmId}`, model);
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to save authentication settings."
        );
      setModel((current) => ({ ...current, ...response.data }));
      setMessage(response.message || "Authentication configuration saved.");
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setBusy(false);
    }
  };

  const testConnection = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const response = await postData(
        `/firmidentity/${firmId}/test-connection`,
        {}
      );
      if (!response?.status)
        throw new Error(response?.message || "Unable to test the connection.");
      setModel((current) => ({ ...current, ...response.data }));
      setMessage(
        response.message || "Microsoft Entra tenant connection verified."
      );
    } catch (testError) {
      setError(testError.message);
    } finally {
      setBusy(false);
    }
  };

  const entraSelected = model.authenticationMode !== "Legacy";
  return (
    <Form onSubmit={save} className="p-3" style={{ maxWidth: 720 }}>
      <h5>Authentication &amp; SSO</h5>
      <p className="text-muted">
        PIERS owns the Microsoft application registration. Configure only your
        organization’s tenant here.
      </p>
      {error && <Alert color="danger">{error}</Alert>}
      {message && <Alert color="success">{message}</Alert>}
      <FormGroup>
        <Label>Authentication Mode</Label>
        {modes.map(([value, label]) => (
          <div className="form-check mb-2" key={value}>
            <Input
              className="form-check-input"
              id={`authentication-mode-${value}`}
              type="radio"
              name="authenticationMode"
              value={value}
              checked={model.authenticationMode === value}
              onChange={(event) =>
                setModel((current) => ({
                  ...current,
                  authenticationMode: event.target.value,
                }))
              }
            />
            <Label
              className="form-check-label"
              for={`authentication-mode-${value}`}
            >
              {label}
            </Label>
          </div>
        ))}
      </FormGroup>
      {entraSelected && (
        <section className="border rounded p-3 mb-3">
          <h6>Microsoft Entra</h6>
          <FormGroup>
            <Label for="entra-tenant-id">Tenant ID</Label>
            <Input
              id="entra-tenant-id"
              value={model.externalTenantId}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              onChange={(event) =>
                setModel((current) => ({
                  ...current,
                  externalTenantId: event.target.value,
                }))
              }
            />
          </FormGroup>
          <FormGroup>
            <Label for="entra-tenant-name">Tenant Name</Label>
            <Input
              id="entra-tenant-name"
              value={model.tenantDisplayName}
              placeholder="CodeCatalysts Private Limited"
              onChange={(event) =>
                setModel((current) => ({
                  ...current,
                  tenantDisplayName: event.target.value,
                }))
              }
            />
          </FormGroup>
          <FormGroup>
            <Label>External ID identity type</Label>
            {[
              ["PiersManaged", "PIERS-managed local account"],
              ["CorporateFederated", "Corporate SSO federated account"],
            ].map(([value, label]) => (
              <div className="form-check mb-2" key={value}>
                <Input
                  className="form-check-input"
                  id={`identity-type-${value}`}
                  type="radio"
                  name="externalIdentityProvisioningMode"
                  value={value}
                  checked={model.externalIdentityProvisioningMode === value}
                  onChange={(event) =>
                    setModel((current) => ({
                      ...current,
                      externalIdentityProvisioningMode: event.target.value,
                    }))
                  }
                />
                <Label
                  className="form-check-label"
                  for={`identity-type-${value}`}
                >
                  {label}
                </Label>
              </div>
            ))}
          </FormGroup>
          {model.externalIdentityProvisioningMode === "CorporateFederated" && (
            <>
              <Alert color="info">
                Each invited corporate user must include their source Entra
                object ID. Email alone cannot establish federated identity.
              </Alert>
              <FormGroup>
                <Label>External ID provider ID</Label>
                <Input
                  value={model.federationProviderId || ""}
                  onChange={(event) =>
                    setModel((current) => ({
                      ...current,
                      federationProviderId: event.target.value,
                    }))
                  }
                />
              </FormGroup>
              <FormGroup>
                <Label>Federation issuer</Label>
                <Input
                  value={model.federationIssuer || ""}
                  onChange={(event) =>
                    setModel((current) => ({
                      ...current,
                      federationIssuer: event.target.value,
                    }))
                  }
                />
              </FormGroup>
              <FormGroup>
                <Label>Customer Entra tenant ID</Label>
                <Input
                  value={model.federationTenantId || ""}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  onChange={(event) =>
                    setModel((current) => ({
                      ...current,
                      federationTenantId: event.target.value,
                    }))
                  }
                />
              </FormGroup>
            </>
          )}
          <p className="mb-1">
            <b>Connection Status:</b> {model.connectionStatus}
          </p>
          {model.connectedAt && (
            <p className="mb-3">
              <b>Last tested:</b> {new Date(model.connectedAt).toLocaleString()}
            </p>
          )}
          <Button
            type="button"
            color="secondary"
            disabled={busy}
            onClick={testConnection}
          >
            Test Connection
          </Button>
        </section>
      )}
      <Button color="primary" disabled={busy} type="submit">
        Save
      </Button>
    </Form>
  );
};

export default FirmAuthenticationSettings;
